import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ImagePlus, Loader2, Video } from 'lucide-react';
import { fetchUrlImages, fetchUrlVideos } from '../../api/posts/fetch-post.api';
import {
  BrandContentFormat,
  BrandDealSummary,
  BrandSponsorCandidate,
  MediaRef,
  SaveBrandDealContentBody,
  getBrandDealContent,
  updateBrandDealContent,
} from '../../api/payment/brand-deal.api';
import { contentFormatHint } from '../../utils/brand-content-format';
import { BRAND_CAMPAIGN_LIMITS } from '../../utils/brand-deal';
import { apiErrorMessage } from '../../utils/api-error-message';
import {
  EXISTING_POST_REQUIRED_MESSAGE,
  EXISTING_POST_RESELECT_NOTE,
  contentVersionLabel,
  directEditCounterLabel,
  existingPostLink,
  validateNewDealContent,
} from '../../utils/brand-deal-content';
import { MediaThumb } from './brand-campaign-form';
import BrandExistingPostPicker, { BrandContentSource, ContentSourceToggle } from './brand-existing-post-picker';

/** Dòng thông tin nội dung của một lời mời trong bảng deal phía người đặt (spec 2026-10-12 §5). */
export const DealContentInfo: React.FC<{ deal: BrandDealSummary; onEdit: () => void }> = ({ deal, onEdit }) => {
  const version = contentVersionLabel(deal.contentVersion);
  const used = deal.directEditCount ?? 0;
  const max = deal.directEditMax ?? 0;
  if (!deal.canEditContent && !version && !deal.existingPostId && used === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
      {version && <span className="ui-muted">{version}</span>}
      {deal.existingPostId && (
        <a href={existingPostLink(deal.existingPostId)} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#0057FF]">
          Xem bài gốc
        </a>
      )}
      {used > 0 && max > 0 && (
        <span className="font-semibold text-amber-600 dark:text-amber-400">{directEditCounterLabel(used, max)}</span>
      )}
      {deal.existingPostUnavailable && <span className="text-rose-600 dark:text-rose-400">{EXISTING_POST_RESELECT_NOTE}</span>}
      {deal.canEditContent && (
        <button type="button" onClick={onEdit} className="rounded-md border ui-divider px-2.5 py-1 font-semibold ui-text">
          Sửa nội dung
        </button>
      )}
    </div>
  );
};

export interface BrandDealContentEditorProps {
  deal: BrandDealSummary;
  campaignId: string;
  contentFormat?: BrandContentFormat | null;
  onClose: () => void;
  onSaved: () => void;
}

interface LoadedSnapshot {
  existingPostId: string | null;
  caption: string;
  mediaKey: string;
}
const EMPTY_SNAPSHOT: LoadedSnapshot = { existingPostId: null, caption: '', mediaKey: '' };
const mediaKey = (images: MediaRef[], videos: MediaRef[]) =>
  `${images.map((i) => i.id || i.url).join(',')}|${videos.map((v) => v.id || v.url).join(',')}`;

/** Người đặt lưu bản nội dung mới cho một lời mời còn chờ: nội dung mới (chữ, ảnh, video) hoặc một bài có sẵn của creator. */
const BrandDealContentEditor: React.FC<BrandDealContentEditorProps> = ({ deal, campaignId, contentFormat, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(deal.contentVersion ?? 1);
  const [source, setSource] = useState<BrandContentSource>('new');
  const [caption, setCaption] = useState('');
  const [images, setImages] = useState<MediaRef[]>([]);
  const [videos, setVideos] = useState<MediaRef[]>([]);
  const [currentPostId, setCurrentPostId] = useState<string | null>(null);
  const [picked, setPicked] = useState<BrandSponsorCandidate | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<LoadedSnapshot>(EMPTY_SNAPSHOT);
  const captionId = `deal-caption-${deal.id}`;
  const hint = contentFormatHint(contentFormat);

  useEffect(() => {
    let alive = true;
    getBrandDealContent(deal.id)
      .then((c) => {
        if (!alive) return;
        setVersion(c.version);
        setLoaded({
          existingPostId: c.existingPostId,
          caption: c.existingPostId ? '' : c.caption.trim(),
          mediaKey: c.existingPostId ? '' : mediaKey(c.images, c.videos),
        });
        if (c.existingPostId) {
          setSource('existing');
          setCurrentPostId(c.existingPostUnavailable ? null : c.existingPostId);
        } else {
          setCaption(c.caption);
          setImages(c.images);
          setVideos(c.videos);
        }
      })
      .catch((err) => {
        if (alive) setLoadError(apiErrorMessage(err, 'Không tải được nội dung lời mời.'));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [deal.id]);

  const onPickImages = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = BRAND_CAMPAIGN_LIMITS.maxImages - images.length;
    if (remaining <= 0) {
      toast.error(`Tối đa ${BRAND_CAMPAIGN_LIMITS.maxImages} ảnh.`);
      return;
    }
    const fd = new FormData();
    Array.from(files)
      .slice(0, remaining)
      .forEach((f) => fd.append('type', f));
    try {
      setUploading(true);
      const res = (await fetchUrlImages(fd)) as MediaRef[];
      if (res?.length) setImages((prev) => [...prev, ...res.map((r) => ({ id: r.id, url: r.url }))]);
    } catch {
      toast.error('Tải ảnh thất bại.');
    } finally {
      setUploading(false);
    }
  };

  const onPickVideo = async (file?: File) => {
    if (!file) return;
    if (videos.length >= BRAND_CAMPAIGN_LIMITS.maxVideos) {
      toast.error(`Tối đa ${BRAND_CAMPAIGN_LIMITS.maxVideos} video.`);
      return;
    }
    const fd = new FormData();
    fd.append('type', file);
    try {
      setUploading(true);
      const res = (await fetchUrlVideos(fd)) as Array<{ id?: string; url?: string; status?: string; error?: string }>;
      const failed = res.filter((v) => v.status === 'Failed' || !v.id);
      const ok = res.filter((v) => v.status !== 'Failed' && v.id);
      if (failed.length) toast.error(failed[0]?.error || 'Tải video thất bại.');
      if (ok.length) setVideos((prev) => [...prev, ...ok.map((v) => ({ id: v.id as string, url: v.url as string }))]);
    } catch {
      toast.error('Tải video thất bại.');
    } finally {
      setUploading(false);
    }
  };

  const unchanged =
    source === 'existing'
      ? loaded.existingPostId !== null && !picked && !!currentPostId
      : loaded.existingPostId === null && caption.trim() === loaded.caption && mediaKey(images, videos) === loaded.mediaKey;

  const save = async () => {
    let body: SaveBrandDealContentBody;
    if (source === 'existing') {
      const postId = picked?.postId ?? currentPostId;
      if (!postId) {
        setError(EXISTING_POST_REQUIRED_MESSAGE);
        return;
      }
      body = { existingPostId: postId };
    } else {
      const problem = validateNewDealContent(contentFormat ?? null, caption, images.length, videos.length);
      if (problem) {
        setError(problem);
        return;
      }
      body = { caption: caption.trim(), images, videos, existingPostId: null };
    }
    setError(null);
    try {
      setSaving(true);
      const saved = await updateBrandDealContent(deal.id, body);
      toast.success(`Đã lưu nội dung bản ${saved.version}. Creator có thêm 24 giờ để xem lại.`);
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, 'Không lưu được nội dung.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Sửa nội dung lời mời">
      <div className="ui-card max-h-[90vh] w-full max-w-xl space-y-3 overflow-y-auto p-4">
        <h3 className="ui-heading text-[15px] font-bold">Sửa nội dung lời mời</h3>
        <p className="text-[12px] ui-muted">
          Đang là bản {version}. Lưu sẽ tạo bản mới, giá không đổi và creator có thêm 24 giờ để xem lại.
        </p>
        {loading ? (
          <Loader2 size={18} className="animate-spin ui-muted" />
        ) : loadError ? (
          <>
            <p className="text-[12px] text-rose-600 dark:text-rose-400">{loadError}</p>
            <button type="button" onClick={onClose} className="rounded-md border ui-divider px-3 py-1.5 text-sm ui-text">
              Đóng
            </button>
          </>
        ) : (
          <>
            <ContentSourceToggle
              value={source}
              onChange={(v) => {
                setSource(v);
                setError(null);
              }}
            />
            {source === 'new' ? (
              <div className="space-y-2">
                <label htmlFor={captionId} className="block text-xs ui-muted">
                  Nội dung bài
                </label>
                <textarea
                  id={captionId}
                  rows={5}
                  value={caption}
                  maxLength={BRAND_CAMPAIGN_LIMITS.captionMaxLength}
                  onChange={(e) => setCaption(e.target.value)}
                  className="w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
                />
                {hint && <p className="text-[11px] ui-muted">{hint}</p>}
                <div className="flex flex-wrap gap-2">
                  {images.map((img) => (
                    <MediaThumb key={img.id || img.url} url={img.url} onRemove={() => setImages((prev) => prev.filter((x) => x !== img))} />
                  ))}
                  {videos.map((v) => (
                    <MediaThumb key={v.id || v.url} url={v.url} isVideo onRemove={() => setVideos((prev) => prev.filter((x) => x !== v))} />
                  ))}
                  {images.length < BRAND_CAMPAIGN_LIMITS.maxImages && (
                    <label
                      aria-label="Thêm ảnh"
                      className="flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-dashed ui-divider"
                    >
                      {uploading ? <Loader2 size={18} className="animate-spin ui-muted" /> : <ImagePlus size={18} className="ui-muted" />}
                      <input type="file" accept="image/*" multiple hidden onChange={(e) => onPickImages(e.target.files)} />
                    </label>
                  )}
                  {videos.length < BRAND_CAMPAIGN_LIMITS.maxVideos && (
                    <label
                      aria-label="Thêm video"
                      className="flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-dashed ui-divider"
                    >
                      <Video size={18} className="ui-muted" />
                      <input type="file" accept="video/*" hidden onChange={(e) => onPickVideo(e.target.files?.[0])} />
                    </label>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {currentPostId && !picked && (
                  <p className="text-xs ui-text">
                    Đang dùng bài có sẵn.{' '}
                    <a href={existingPostLink(currentPostId)} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#0057FF]">
                      Xem bài gốc
                    </a>
                  </p>
                )}
                {deal.existingPostUnavailable && <p className="text-xs text-rose-600 dark:text-rose-400">{EXISTING_POST_RESELECT_NOTE}</p>}
                <BrandExistingPostPicker
                  campaignId={campaignId}
                  creatorId={deal.creatorId}
                  selectedPostId={picked?.postId ?? currentPostId}
                  onSelect={setPicked}
                />
              </div>
            )}
            {error && <p className="text-[12px] text-rose-600 dark:text-rose-400">{error}</p>}
            {unchanged && <p className="text-[12px] ui-muted">Nội dung chưa thay đổi.</p>}
            <div className="flex gap-2">
              <button
                type="button"
                disabled={saving || uploading || unchanged}
                onClick={save}
                className="rounded-md bg-[#0057FF] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {saving ? 'Đang lưu…' : 'Lưu bản mới'}
              </button>
              <button type="button" disabled={saving} onClick={onClose} className="rounded-md border ui-divider px-3 py-1.5 text-sm ui-text">
                Đóng
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BrandDealContentEditor;
